/**
 *
 *  Manage Membership Modal
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 08/07/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import moment from "moment";

import useTheme from "../../ThemeProvider/useTheme/useTheme";
import { useRegion } from "../../../context/RegionProvider";

// Components
import Button from "../../Button/Button";

// Icons
import ArrowRightIcon from "../../../icons/ArrowRightIcon";
import PhoneIcon from "../../../icons/PhoneIcon";
import EnvelopeIcon from "../../../icons/EnvelopeIcon";
import PauseIcon from "../../../icons/PauseIcon";
import PlayIcon from "../../../icons/PlayIcon";

// Utils
import { clientWidth } from "../../../utils";
import { modalStyles } from "../../../constants";
import { getMomentWithTz } from "../../../utils/date";
import CloseIcon from "../../../icons/CloseIcon";

// Images

const ManageMembershipModal = ({
  isOpen,
  showCta,
  membershipInfo,
  // region,
  onCancelMembershipClick,
  onPauseMembershipClick,
  onClose,
}) => {
  const { theme } = useTheme();
  const { region } = useRegion();

  const customStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? modalStyles.newMobileDefault
      : {
          ...modalStyles.default,
          content: {
            ...modalStyles.default.content,
            padding: "32px",
            // zIndex: 1060,
          },
        };

  Object.assign(
    {},
    { ...modalStyles.default },
    {
      overlay: { ...modalStyles.default.overlay, zIndex: 1060 },
    }
  );

  const [isModalOpen, setIsModalOpen] = useState();

  const modalResolveRef = React.useRef();

  const openModal = () => {
    return new Promise((resolve) => {
      modalResolveRef.current = resolve;

      setIsModalOpen(true);
    });
  };

  const closeModal = () => {
    if (modalResolveRef.current) {
      modalResolveRef.current();
      modalResolveRef.current = null;

      onClose();
    }

    setIsModalOpen(false);
  };

  const cancelAtPeriodEnd = membershipInfo?.metadata.cancelAtPeriodEnd;
  var sessionsLeft = 0;
  var subscriptionCost = 0;

  if (membershipInfo) {
    let price = (membershipInfo.amount / 100).toFixed(2);

    // Remove decimal and trailing zeroes if no fractional value
    price = price.replace(/\.00$/, "");
    subscriptionCost = `${
      membershipInfo.currency === "USD" ? "$" : ""
    }${price}`;

    sessionsLeft =
      membershipInfo?.metadata.totalEvents -
      membershipInfo?.metadata.usedEvents;
  }

  const isPaused = membershipInfo?.metadata.isPaused;

  const expiresDate = getMomentWithTz(
    moment.utc(membershipInfo?.metadata.currentPeriodEnd).toDate(),
    region
  );
  const resumeAtDate = getMomentWithTz(
    moment.utc(membershipInfo?.metadata.resumeAt).toDate(),
    region
  );
  const afterRenewalDate = getMomentWithTz(new Date(), region).isAfter(
    expiresDate
  );

  return (
    <Modal
      appElement={/*__CLIENT__ && */ document.getElementById("#app")}
      isOpen={isOpen}
      // onRequestClose={closeModal}
      style={customStyle}
      contentLabel="Manage Membership modal"
    >
      <div
        role="button"
        className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-p-1"
        onClick={onClose}
      >
        <CloseIcon className="tbk-p-0 tbk-text-coral" />
      </div>

      <div className="modal-manage-subscription tbk-flex tbk-h-full tbk-flex-col tbk-items-center tbk-rounded-2xl tbk-bg-basic-white tbk-p-3 tbk-text-center lg:tbk-p-0">
        <div className="tbk-mb-4 tbk-grow">
          <div className="tbk-text-title-bold tbk-mt-5_ tbk-mb-4 tbk-text-primary">
            Membership Details
          </div>

          <div className="tbk-text-main tbk-text-blue-grey">
            {isPaused
              ? afterRenewalDate
                ? "Paused until"
                : "Pauses"
              : cancelAtPeriodEnd
              ? "Expires"
              : "Renewal date"}
            :{" "}
            {isPaused && afterRenewalDate
              ? resumeAtDate.format("MMM DD, YYYY") //expiresDate.add(1, "month").format("MMM DD, YYYY")
              : expiresDate.format(
                  "MMM DD, YYYY" /*region === "GB" ? "DD.MM.YYYY" : "MM.DD.YYYY"*/
                )}
          </div>
          <div className="tbk-text-main tbk-text-blue-grey">
            {/*Renewal date: {getMomentInLosAngelesTimeZone(membershipInfo.currentPeriodEnd).format("MM.DD.YYYY")}*/}
            Subscription cost: {subscriptionCost}
          </div>
          <div className="tbk-text-main tbk-text-blue-grey">
            Sessions left: {sessionsLeft} out of{" "}
            {membershipInfo?.metadata.totalEvents}
          </div>
        </div>

        {!cancelAtPeriodEnd ? (
          <Button
            variant="secondary"
            size="md"
            className={`${isPaused ? "tbk-w-70" : "tbk-w-60"} tbk-mb-4`}
            startIcon={isPaused ? <PlayIcon /> : <PauseIcon />}
            onClick={onPauseMembershipClick}
          >
            {isPaused ? "Re-enable Subscription" : "Pause Subscription"}
          </Button>
        ) : null}

        {showCta && !cancelAtPeriodEnd ? (
          <button
            className="tbk-w-full tbk-text-primary tbk-underline hover:tbk-text-grey-inactive"
            onClick={onCancelMembershipClick}
          >
            Cancel Subscription
          </button>
        ) : null}
      </div>
    </Modal>
  );
};

ManageMembershipModal.displayName = "ManageMembershipModal";
ManageMembershipModal.propTypes = {
  heroRef: PropTypes.object.isRequired,
};
ManageMembershipModal.defaultProps = {
  showLinks: true,
  showMenu: true,
};

export default ManageMembershipModal;
